'use client'

import { useState, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { Grid, Container, Button, Typography, Alert } from '@mui/material'
import { useSnackbar } from 'notistack'

// Types
import type { TimelineProps } from '@/types/components/pages/timeline'
import type { APIArticleItem } from '@/types/api/items/article/article'
import type { APIArticleReadResult } from '@/types/api/(article)/article/read'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Utils
import URLParams from '@/utils/url-params'

// Fonts


// Components
const StepperMolecule = dynamic(() => import('@/components/molecules/timeline/stepper'))
const TimelineItemMolecule = dynamic(() => import('@/components/molecules/timeline/item'))

const TimelinePage = (props: TimelineProps) => {
	// Props
	const { lng, menu, articles, years, activeYear } = props

	// Variables
	const { t } = useTranslation(lng)
	const { enqueueSnackbar } = useSnackbar()
	const [year, setYear] = useState<number>(activeYear ?? 1)
	const [items, setItems] = useState<APIArticleItem[]>(articles ?? [])
	const [loading, setLoading] = useState<boolean>(false)
	const thisYear = new Date().getFullYear()

	// Callbacks
	const handleChangeYear = useCallback(
		async (newYear: string) => {
			// Check year
			if (['pre-history', '1700', '1800', '1900', '1925', '1950', '1975', '2000', '2005', '2010', '2015', '2020', thisYear.toString(), 'show-all'].includes(newYear)) {
				// Find year
				let checkYear: number = 1
				let field_year: string = '1700,1800'

				if (newYear === 'pre-history') {
					checkYear = 0
					field_year = '0,1699'
				} else if (newYear === 'show-all') {
					checkYear = Infinity
					field_year = `0,${thisYear}`
				} else {
					checkYear = years.findIndex(item => item === parseInt(newYear ?? '1700'))
					field_year = `${years[checkYear]},${checkYear + 1 < years.length ? years[checkYear + 1] - 1 : thisYear}`
				}

				// Set loading enabled
				setLoading(true)

				// Response
				const res = await fetch(`/api/article/read?${URLParams({ category_alias: menu.type_value ?? '', field_year, order: 'field_year', direction: '1', limit: '100' })}`, {
					method: 'GET',
					headers: { 'Accept-Language': lng, 'Content-Type': 'application/json' }
				})

				// Result
				const data: APIArticleReadResult = await res.json()

				// Set loading disabled
				setLoading(false)

				// Check result
				if (data.status && data.data) {
					setItems(data.data.items ?? [])
					setYear(checkYear)
					// router.push(`/${lng}/timeline${newYear !== '1700' ? `?year=${newYear}` : ''}`)
				}

				// Check messages
				if (data.message) enqueueSnackbar(data.message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 })
				if (data.messages) data.messages.map(message => enqueueSnackbar(message, { variant: data.status ? 'success' : 'error', autoHideDuration: 6000 }))
			}
		},
		[lng, thisYear, menu, years, enqueueSnackbar]
	)

	return (
		<Grid container>
			<Grid display="inline-flex" justifyContent="end" alignItems="start" size={{ xs: 12, md: 3, lg: 2 }} p={{ xs: 2, md: 5 }} bgcolor="#fdfaf3">
				<Grid container width={{ md: 140 }} alignItems="center" spacing={2} position={{ md: 'sticky' }} top={{ md: 94, lg: 110, xl: 130 }}>
					<Grid display="inline-flex" justifyContent="center" size={{ xs: 'auto', md: 12 }}>
						<Button loadingPosition="start" loading={loading} onClick={() => handleChangeYear('pre-history')}>
							{t('common:title.preHistory')}
						</Button>
					</Grid>

					<Grid display="inline-flex" justifyContent="center" size={{ xs: 'grow', md: 12 }} ml={{ md: 4.5 }}>
						<StepperMolecule items={years} activeStep={year} loading={loading} onClick={handleChangeYear} />
					</Grid>

					<Grid display="inline-flex" justifyContent="center" size={{ xs: 'auto', md: 12 }}>
						<Button loadingPosition="start" loading={loading} onClick={() => handleChangeYear('show-all')}>
							{t('common:title.showAll')}
						</Button>
					</Grid>
				</Grid>
			</Grid>

			<Grid size={{ xs: 12, md: 9, lg: 10 }} px={{ md: 2 }}>
				<Container>
					<Grid container spacing={{ xs: 2, md: 'inherit' }}>
						<Grid container spacing={{ xs: 2, md: 'inherit' }}>
							<Grid size={12} my={{ xs: 4, md: 8 }}>
								<Typography
									component="h2"
									fontWeight={400}
									fontSize={{ xs: 24, md: 48 }}
									lineHeight={1.25}
									textAlign="center"
									color="primary.main"
								>
									{year === 0
										? t('common:title.preHistory')
										: year === Infinity
											? t('common:title.showAll')
											: year + 1 === years.length
												? t('common:title.now')
												: `${years[year]} - ${years[year + 1]}`}
								</Typography>
							</Grid>

							{items.map((article, index) => {
								let image: string = article.image

								// Fields Values
								const fv = article.fields_values
								let year: number = 0
								let description: string = ''

								if (Array.isArray(fv) && fv.length > 5) {
									year = parseInt(fv[0].value || '0')
									image = fv[2].value ?? article.image
									description = `${fv[4].value}<br />${fv[5].value}`
								}

								return (
									<TimelineItemMolecule
										key={index}
										lng={lng}
										reverse={index % 2 === 1}
										title={article.title}
										link={`/article/${article.alias}`}
										year={year}
										image={image}
										description={description}
										isFirst={index === 0}
										isLast={index + 1 === items.length}
									/>
								)
							})}

							{items.length === 0 && (
								<Grid size={12}>
									<Alert severity="warning">{t('common:description.notFoundItems')}</Alert>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Container>
			</Grid>
		</Grid>
	)
}

export default TimelinePage
